import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import type { AttentionSummary, DeliverableView, ProjectView, TaskView } from "@fieldpath/shared";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { api } from "./api/client";
import type { Fetcher } from "./api/client";
import { loginRequest } from "./auth/config";
import { useApi } from "./auth/useApi";
import { Help } from "./components/Help";
import { Home } from "./components/Home";
import { InboxButton } from "./components/InboxButton";
import { PastDatePrompt } from "./components/PastDatePrompt";
import { ReportProblem } from "./components/ReportProblem";
import { Inbox } from "./components/Inbox";
import { DeliverableDetail } from "./components/DeliverableDetail";
import { ProjectDetail } from "./components/ProjectDetail";
import { ProjectList } from "./components/ProjectList";
import { Admin } from "./components/Admin";
import { PunchList } from "./components/PunchList";
import { Overview } from "./components/Overview";
import { NeedsAttentionPage } from "./components/NeedsAttention";
import { Workload } from "./components/Workload";
import { FinishedWorkPage } from "./components/FinishedWork";
import { TaskDetail } from "./components/TaskDetail";
import { UpdateBar } from "./components/UpdateBar";
import { CanopyScenes } from "./canopyScenes";
import { schedulerUrl, wrikeArchiveUrl } from "./config";
import type { Route } from "./router";
import { navTabFor, readRememberedTab, rememberTab, type NavTab } from "./navTab";
import { useRefreshOnReturn } from "./refreshOnReturn";
import {
  navigate,
  rememberReturnPath,
  shareUrl,
  useRoute,
  useSettledLoading,
} from "./router";

/**
 * A face, no rules. Every decision — what a valid outcome is, whether a reason
 * is required, what status a task is in — belongs to the API, which is the only
 * thing that touches the database.
 */
export function App() {
  return (
    <>
      <UnauthenticatedTemplate>
        <BareHeader />
        <main className="wrap">
          <SignIn />
        </main>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <SignedIn />
      </AuthenticatedTemplate>
      {/* A newer build is being served (punch list F173). */}
      <UpdateBar />
    </>
  );
}

/**
 * The header with nowhere to go: before sign-in, while the API is asked who
 * this is, and for somebody it will not let in. `right` is that last screen's
 * Report a problem and Sign out.
 */
function BareHeader({ right }: { right?: ReactNode }) {
  return (
    <header className="app">
      {/* The same brand group, not a link: there is nowhere to go from
          these screens. It is the first thing anyone sees, and a
          plain word where the rest of the app shows a logo reads as a
          different, lesser application. */}
      <div className="wrap row spread">
        <span className="brand">
          <img className="erp-mark" src="/erp-logo.png" alt="Energy Renewal Partners" />
          <span className="brand-rule" aria-hidden="true" />
          {/* The mark is decorative — `alt=""` — because the word image
              beside it carries `alt="Canopy"`. One accessible name, not
              two: a screen reader saying "Canopy Canopy" is worse than
              saying it once.

              The h1 stays. These screens have no other h1, and the word
              image is it — so the wrapper is reset to `line-height: 0` in
              the stylesheet rather than deleted. */}
          <img className="product-mark" src="/canopy-mark.png" alt="" />
          <h1 className="brand-name">
            <img className="canopy-word" src="/canopy-word.png" alt="Canopy" />
          </h1>
        </span>
        {right ? <div className="row">{right}</div> : null}
      </div>
    </header>
  );
}

function SignIn() {
  const { instance } = useMsal();
  return (
    <div className="card">
      <h2>Sign in</h2>
      <p className="muted">Canopy uses your Microsoft 365 account.</p>
      <button
        className="primary"
        onClick={() => {
          // Remembered before the redirect, because Entra returns to the site
          // root and someone who opened a task link would otherwise lose it.
          rememberReturnPath();
          void instance.loginRedirect(loginRequest);
        }}
      >
        Sign in with Microsoft
      </button>
    </div>
  );
}

type Me = Awaited<ReturnType<typeof api.me>>;

/**
 * Who the API says this is, asked before anything else.
 *
 * Somebody whose sign-in matches no Deltek record gets ONE screen and none of
 * the app (Jeremy, 22 Oct 2026: unlinked sees nothing in either app). The API
 * refuses them everything else regardless (linked-guard.ts); asking first and
 * alone is what keeps the app's own calls from starting for them at all.
 */
function SignedIn() {
  const fetcher = useApi();
  const [me, setMe] = useState<Me | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void api.me(fetcher).then(setMe).catch(() => setFailed(true));
  }, [fetcher]);

  if (me && me.employee === null) return <NotLinked fetcher={fetcher} email={me.user.email} />;
  // A failed check is not evidence about anybody, so it falls through to the
  // app, where the API still answers every call on its own terms.
  if (me || failed) return <Workspace me={me} />;
  return <BareHeader />;
}

/** The whole of Canopy for a sign-in with no Deltek record: why, and who fixes it. */
function NotLinked({ fetcher, email }: { fetcher: Fetcher; email: string | null }) {
  return (
    <>
      <BareHeader
        right={
          <>
            <ReportProblem fetcher={fetcher} />
            <SignOut />
          </>
        }
      />
      <main className="wrap">
        <div className="card not-linked">
          <h2>Your sign-in isn't linked to Deltek</h2>
          <p>
            You signed in as <strong>{email ?? "an account with no email address"}</strong>. Canopy
            matches that address to your employee record in Deltek and found no match, so you can't
            use Canopy, here or in the resource scheduler, until it does.
          </p>
          <p>
            HR fixes this by correcting your email address in Deltek. Canopy picks the change up
            with the next Deltek sync, overnight; reload this page after that.
          </p>
          <p className="muted">
            If your address in Deltek is already right, tell us with <strong>Report a problem</strong>{" "}
            above.
          </p>
        </div>
      </main>
    </>
  );
}

function SignOut() {
  const { instance, accounts } = useMsal();
  const who = accounts[0]?.name ?? accounts[0]?.username;
  // The name shows only where the bar has room for it (F125, 29 Oct 2026:
  // the header wrapped on most laptops); the button names who is signed in
  // everywhere.
  return (
    <div className="row">
      <span className="muted signed-in-name">{who}</span>
      <button
        className="report-open"
        title={who ? `Signed in as ${who}` : undefined}
        onClick={() => void instance.logoutRedirect()}
      >
        Sign out
      </button>
    </div>
  );
}

/**
 * Header and body together, because a mention in the header has to be able to
 * open a task in the body. Splitting them would mean lifting navigation into a
 * context that spans the two, which is more machinery than four views justify.
 *
 * The URL is the navigation state. Everything on screen is derived from it, so
 * a task has an address that can be pasted to someone, refresh keeps your
 * place, and Back does what Back should.
 */
function Workspace({ me }: { me: Me | null }) {
  const fetcher = useApi();
  const route = useRoute();

  // What the URL points AT, fetched rather than carried. A pasted link arrives
  // with nothing but an id, so loading by id is the general case and passing a
  // whole object through navigation would only work for the local one.
  const [project, setProject] = useState<ProjectView | null>(null);
  const [task, setTask] = useState<TaskView | null>(null);
  const [deliverable, setDeliverable] = useState<DeliverableView | null>(null);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState<string | null>(null);
  // Bumped whenever something may have changed the unread count, so the
  // header badge does not wait for its next poll.
  const [inboxTick, setInboxTick] = useState(0);
  // Null until the check resolves. The server is authoritative either way —
  // this governs whether the Overview is OFFERED, so a wrong answer hides a
  // menu item rather than granting anything.
  const [isPm, setIsPm] = useState<boolean | null>(null);
  // Same arrangement as isPm: the server refuses non-admins on every
  // admin route regardless, so a wrong answer here hides a menu item
  // rather than granting anything.
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  // The punch list's members (27 Oct 2026), the same arrangement again: the
  // server refuses everybody else on every punch route.
  const [isPunchMember, setIsPunchMember] = useState(false);
  // The flag beside "Needs attention" (A.1): whether to offer the page at
  // all, the red problem count and the yellow concern dot. Asked on sign-in,
  // every five minutes, and on arriving at or leaving the page, so fixing
  // something there shows in the flag without waiting for the next poll.
  const [attention, setAttention] = useState<AttentionSummary | null>(null);
  const onAttentionPage = route.kind === "attention";
  // Coming back to the tab (punch list F172): the flag is asked again whatever
  // is open; the page's own data only when nothing is being edited.
  const backCounts = useRefreshOnReturn({ whileEditing: true });
  const back = useRefreshOnReturn();
  useEffect(() => {
    const ask = () => void api.attentionSummary(fetcher).then(setAttention).catch(() => undefined);
    ask();
    const timer = window.setInterval(ask, 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [fetcher, onAttentionPage, backCounts]);

  // Asked once, for the header. The Overview refuses non-PMs server-side
  // regardless; this only decides whether the menu item is there to click.
  useEffect(() => {
    // Returns false rather than throwing for the 403 that most people get,
    // because not being an admin is the ordinary case and not an error.
    void api.amIAdmin(fetcher).then(setIsAdmin).catch(() => setIsAdmin(null));
    void api.punchMe(fetcher).then((m) => setIsPunchMember(m.member)).catch(() => setIsPunchMember(false));
  }, [fetcher]);

  // Null when SignedIn's check failed, and then unknown rather than "no".
  const myKey = me?.employee?.ajeraKey ?? null;
  useEffect(() => {
    if (myKey === null) return;
    void api
      .projectManagers(fetcher)
      .then((pms) => setIsPm(pms.some((p) => p.key === myKey)))
      // Unknown, not "no": a failed check is not evidence about anybody.
      .catch(() => setIsPm(null));
  }, [fetcher, myKey]);

  // Only call it loading once it has actually taken a moment. Most
  // navigations finish faster than a spinner is worth showing.
  const busy = useSettledLoading(loading);

  useEffect(() => {
    let cancelled = false;
    setMissing(null);

    const load = async (): Promise<void> => {
      // The resource scheduler's "Open in Fieldpath": the task linked to that deployment, opened
      // in place of this address; or a plain answer that none is linked yet.
      if (route.kind === "mobilization") {
        setLoading(true);
        try {
          const taskId = await api.taskForMobilization(fetcher, route.id);
          if (cancelled) return;
          if (taskId) navigate({ kind: "task", id: taskId }, true);
          else setMissing("No Fieldpath task is linked to that deployment yet. Link it from its Field Mobilization task's Mobilization panel.");
        } catch {
          if (!cancelled) setMissing("That deployment could not be looked up.");
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }
      if (route.kind === "task") {
        setLoading(true);
        try {
          const found = await api.getTask(fetcher, route.id);
          const parent = await api.getProject(fetcher, found.projectId);
          if (cancelled) return;
          setTask(found);
          setProject(parent);
        } catch {
          if (cancelled) return;
          // A link to something deleted, or to another tenant's id. Say so —
          // a blank screen reads as the app being broken.
          setTask(null);
          setMissing("That task no longer exists, or you do not have access to it.");
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (route.kind === "deliverable") {
        setLoading(true);
        try {
          const found = await api.getDeliverable(fetcher, route.id);
          // The parent too, so the trail names it. Without this a pasted
          // deliverable link shows no project, or worse, whichever project
          // happened to be on screen before.
          const parent = await api.getProject(fetcher, found.projectId);
          if (cancelled) return;
          setDeliverable(found);
          setProject(parent);
          setTask(null);
        } catch {
          if (cancelled) return;
          setDeliverable(null);
          setMissing(
            "That deliverable no longer exists, or you do not have access to it.",
          );
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (route.kind === "project") {
        setLoading(true);
        try {
          const parent = await api.getProject(fetcher, route.id);
          if (cancelled) return;
          setProject(parent);
          setTask(null);
        } catch {
          if (cancelled) return;
          setProject(null);
          setMissing("That project no longer exists, or you do not have access to it.");
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      setTask(null);
      setProject(null);
      setDeliverable(null);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [route, fetcher, back]);

  // The underlined header tab: the page's own, or on a project, deliverable or
  // task page the tab it was opened from (punch list F109), per browser tab.
  const [lastTab, setLastTab] = useState<NavTab | null>(readRememberedTab);
  const onTab = navTabFor(route, lastTab);
  useEffect(() => {
    const own = navTabFor(route, null);
    if (own) {
      setLastTab(own);
      rememberTab(own);
    }
  }, [route]);

  /* ── The Canopy easter egg ───────────────────────────────────────────────
     Clicking the trees plays one of eight short scenes over the header (the
     soil scientists and the owl were added 16 Oct 2026, beyond the handoff,
     and stay inside the header). Design
     handoff in `docs/design_handoff_canopy_logo_animation/`; the engine is
     `canopyScenes.js`, copied from it unchanged and deliberately not typed.

     It is created ONCE, on mount, and lives in a ref rather than in state:
     the instance owns a `requestAnimationFrame` loop and a canvas, and
     rebuilding it on every route change would cancel a scene the moment
     somebody navigated -- which is exactly what clicking the brand does. */
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const markRef = useRef<HTMLImageElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const scenesRef = useRef<CanopyScenes | null>(null);

  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    const logo = markRef.current;
    const header = headerRef.current;
    if (!stage || !canvas || !logo || !header) return;

    const scenes = new CanopyScenes({
      stage,
      canvas,
      logo,
      /* The bottom-most pixel ROW of the header, which is where the animals
         walk. Measured rather than written as 71: `header.app` carries a 1px
         `border-bottom` on top of the 72px bar, so `offsetHeight` is 73 here
         and the handoff's own "(71 at 72px)" would land a pixel high. The
         subtraction is what makes the formula right either way. */
      groundY: header.offsetHeight - 1,
      /* Left at the default, FALSE, which is the handoff's choice and
         Jeremy's: a scene only ever runs on a deliberate click, so nothing
         moves that was not asked for. That is the difference from the "mine"
         cue, which pulses at you unbidden and therefore does drop its
         animation under `prefers-reduced-motion` (index.css). */
      respectReducedMotion: false,
    });
    scenesRef.current = scenes;
    return () => {
      scenes.stop();
      scenesRef.current = null;
    };
  }, []);

  return (
    <>
      {/* The stage is the FIRST child and `position: fixed`, so a scene plays
          over the visible header however far the page is scrolled. It never
          takes a click -- `pointer-events: none` -- and it is `aria-hidden`
          because it says nothing a screen reader has any use for. */}
      <div className="canopy-stage" ref={stageRef} aria-hidden="true">
        <canvas ref={canvasRef} />
      </div>
      <header className="app" ref={headerRef}>
        {/* Matches the body's width, or the header would sit narrower than
            the content beneath it on the task page. */}
        <div className="wrap row spread">
          {/* `nav-row` rather than `row`: it stretches to the header's full
              height, which is what lets the active item's lime underline sit
              ON the header's bottom edge rather than floating above it. */}
          <div className="nav-row">
            {/* Logo, a hairline divider, then the product name — one control,
                because they read as one thing and a person aiming at "the logo"
                means "take me home".

                THE NAME IS AN IMAGE, not text, since 9 Oct 2026 — and not an
                h1 here. The header used to carry an h1 while every page below
                it carried its own, so each screen had two. The page's own
                heading is the real one; the word image carries `alt="Canopy"`
                so the accessible name survives the change.

                CANOPY, not Fieldpath: the company's name for this application
                and the resource scheduler together. Display only — every
                identifier is still Fieldpath.

                Two images where there was one illustration and a word. The
                first landscape mark replaced a detailed wildlife drawing that
                needed 48px to stay legible; this one is drawn for the size and
                sits at 52px, with the lettering as a separate 22px image so
                the two can be spaced independently. */}
            {/* TWO BUTTONS SINCE 10 OCT 2026, and the rule above is the
                reason they both go home rather than the reason they are one
                control. The easter egg needs a target of its own; a nested
                button is invalid HTML, so the group had to split. What was
                NOT allowed to change is that a person aiming at the logo
                gets taken home -- so BOTH halves navigate, and the Canopy
                half plays a scene as well. Jeremy's call, asked rather than
                assumed, because the handoff proposed making the Canopy half
                stop navigating.

                `.brand` keeps `display: inline-flex` and its 16px gap, so
                the three children space exactly as the four did. */}
            <div className="brand">
              <button
                className="brand-erp"
                onClick={() => navigate({ kind: "home" })}
                title="Canopy home"
              >
                <img className="erp-mark" src="/erp-logo.png" alt="Energy Renewal Partners" />
              </button>
              <span className="brand-rule" aria-hidden="true" />
              <button
                className="brand-canopy"
                onClick={() => {
                  navigate({ kind: "home" });
                  /* Optional-chained: the engine is null for the first frame
                     after mount and after unmount, and a brand click that
                     silently skips its animation is better than one that
                     throws on the way home. */
                  scenesRef.current?.play();
                }}
                title="Canopy home"
                /* Named for what it DOES, not for the picture on it. The two
                   buttons would otherwise both announce as the home link
                   with no way to tell them apart. */
                aria-label="Canopy home"
              >
                <img className="product-mark" ref={markRef} src="/canopy-mark.png" alt="" />
                <img className="canopy-word" src="/canopy-word.png" alt="Canopy" />
              </button>
            </div>
            <nav className="nav">
              <button
                className={onTab === "home" ? "on" : ""}
                onClick={() => navigate({ kind: "home" })}
              >
                My work
              </button>
              <button
                className={onTab === "projects" ? "on" : ""}
                onClick={() => navigate({ kind: "projects" })}
              >
                Projects
              </button>
              <button
                className={onTab === "workload" ? "on" : ""}
                onClick={() => navigate({ kind: "workload" })}
              >
                Workload
              </button>
              {/* Offered to PMs, admins and anybody who manages work -- the
                  server says which (A.1). The flag beside it is there to be
                  read at a glance: a red count of problems, a yellow dot for
                  any concern. */}
              {attention?.visible ? (
                <button
                  className={onTab === "attention" ? "on" : ""}
                  onClick={() => navigate({ kind: "attention" })}
                  aria-label={`Needs attention${attention.problems > 0 ? `, ${attention.problems} problem${attention.problems === 1 ? "" : "s"}` : ""}${attention.concerns > 0 ? ", concerns" : ""}`}
                >
                  Needs attention
                  {attention.problems > 0 ? (
                    <span className="attn-count" aria-hidden="true">
                      {attention.problems}
                    </span>
                  ) : null}
                  {attention.concerns > 0 ? <span className="attn-dot" aria-hidden="true" /> : null}
                </button>
              ) : null}
              {/* Offered to PMs and admins (13 Oct 2026). Hidden rather than
                  disabled: a control you can see but never use is a question
                  nobody can answer. */}
              {isPm === true || isAdmin === true ? (
                <button
                  className={onTab === "overview" ? "on" : ""}
                  onClick={() => navigate({ kind: "overview" })}
                >
                  Overview
                </button>
              ) : null}
              {/* Offered only to admins, for the same reason as the
                  Overview: a control you can see but never use is a question
                  nobody can answer. */}
              {isAdmin === true ? (
                <button
                  className={onTab === "admin" ? "on" : ""}
                  onClick={() => navigate({ kind: "admin" })}
                >
                  Admin
                </button>
              ) : null}
              {/* The punch list (Jeremy, 27 Oct 2026): only for the people on
                  its members setting, hidden from everybody else for the same
                  reason as Admin. */}
              {isPunchMember ? (
                <button
                  className={onTab === "punchlist" ? "on" : ""}
                  onClick={() => navigate({ kind: "punchlist", label: null })}
                >
                  Punch list
                </button>
              ) : null}
              {/* The scheduler is a SEPARATE application on its own origin,
                  with its own app registration — deliberately, so neither app
                  can mint tokens for the other. Same tenant, so sign-in is
                  silent and this feels like one system.

                  A link, never an iframe: MSAL renews tokens through a hidden
                  iframe that needs third-party cookies, which incognito blocks
                  by default — and incognito is how we demo several people at
                  once. Framing another app inside this one stacks a second
                  cookie-partitioning problem on top of that.

                  SAME TAB, as the scheduler's link back is. Until 30 Oct 2026
                  that was what kept sign-in: MSAL's cache was per tab, so a new
                  tab meant signing in again. It is per browser since (see
                  auth/config.ts), so a new tab would be signed in too.

                  It never takes the `on` state: you are never "in" the
                  scheduler while looking at Fieldpath. */}
              <a className="nav-external" href={schedulerUrl}>
                Scheduler<span aria-hidden="true"> ↗</span>
              </a>
              {/* The Wrike archive (Jeremy, 2 Nov 2026), last in the row. A
                  NEW tab, unlike the scheduler: it is looked things up in
                  beside the work, and it signs in on its own. */}
              <a className="nav-external" href={wrikeArchiveUrl} target="_blank" rel="noopener noreferrer">
                Wrike archive<span aria-hidden="true"> ↗</span>
              </a>
            </nav>
          </div>
          {/* `header-tools`: one quiet size for all four controls, and it sits
              on the RIGHT if a narrow window ever makes the bar wrap (punch
              list F125, 29 Oct 2026). */}
          <div className="row header-tools">
            {/* Help (Jeremy, 19 Oct 2026; built 22 Oct): every article, with
                search. A link, so it can be opened in a new tab beside the
                screen it is about. Only here, for people linked to Deltek:
                NotLinked above deliberately has no Help, because the articles
                describe screens that person cannot open, and its card already
                says the one thing they can do. */}
            <a
              className={route.kind === "help" ? "report-open help-open on" : "report-open help-open"}
              href="/help"
              onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                navigate({ kind: "help", id: null, screen: null });
              }}
            >
              Help
            </a>
            {/* On every screen, one click, never a separate site. The reports
                lost to friction are disproportionately the small ones that turn
                out to be patterns. */}
            <ReportProblem fetcher={fetcher} />
            <InboxButton fetcher={fetcher} refreshKey={inboxTick} />
            <SignOut />
          </div>
        </div>
      </header>

      {/* ONE width for every screen. The task page used to get more room
          than the rest because it shows a whole workflow at once; after the
          refresh every screen is rows or two columns, so there is no longer a
          set of screens that reads better narrow. */}
      <main className="wrap">
        {missing ? (
          <div className="card">
            <h2>Not found</h2>
            <p className="muted">{missing}</p>
            <button className="primary" onClick={() => navigate({ kind: "home" })}>
              Go to my work
            </button>
          </div>
        ) : busy && !loaded(route, task, project, deliverable) ? (
          <p className="muted">Loading…</p>
        ) : route.kind === "task" && task ? (
          <TaskDetail
            fetcher={fetcher}
            task={task}
            shareUrl={shareUrl(route)}
            projectName={project?.name ?? null}
            onBack={() =>
              project
                ? navigate({ kind: "project", id: project.id })
                : navigate({ kind: "home" })
            }
          />
        ) : route.kind === "inbox" ? (
          <Inbox fetcher={fetcher} onChanged={() => setInboxTick((n) => n + 1)} />
        ) : route.kind === "deliverable" && deliverable ? (
          <DeliverableDetail
            fetcher={fetcher}
            deliverable={deliverable}
            projectName={project?.name ?? null}
            onChanged={() =>
              void api
                .getDeliverable(fetcher, deliverable.id)
                .then(setDeliverable)
                .catch(() => undefined)
            }
          />
        ) : route.kind === "project" && project ? (
          <ProjectDetail
            fetcher={fetcher}
            project={project}
            onChanged={() =>
              void api
                .getProject(fetcher, project.id)
                .then(setProject)
                .catch(() => undefined)
            }
            onBack={() => navigate({ kind: "projects" })}
            onOpenTask={(t) => navigate({ kind: "task", id: t.id })}
          />
        ) : route.kind === "help" ? (
          <Help id={route.id} screen={route.screen} />
        ) : route.kind === "admin" ? (
          <Admin fetcher={fetcher} />
        ) : route.kind === "punchlist" ? (
          <PunchList fetcher={fetcher} label={route.label} />
        ) : route.kind === "attention" ? (
          <NeedsAttentionPage fetcher={fetcher} isAdmin={isAdmin === true} />
        ) : route.kind === "overview" ? (
          <Overview
            fetcher={fetcher}
            onOpenTask={(id) => navigate({ kind: "task", id })}
            onOpenProject={(id) => navigate({ kind: "project", id })}
          />
        ) : route.kind === "finished" ? (
          <FinishedWorkPage fetcher={fetcher} onOpenTask={(id) => navigate({ kind: "task", id })} />
        ) : route.kind === "workload" ? (
          <Workload fetcher={fetcher} onOpenTask={(id) => navigate({ kind: "task", id })} />
        ) : route.kind === "mobilization" ? (
          <p className="muted">Finding the task linked to that deployment…</p>
        ) : route.kind === "projects" ? (
          <ProjectList
            fetcher={fetcher}
            onOpen={(p) => navigate({ kind: "project", id: p.id })}
          />
        ) : (
          <Home
            fetcher={fetcher}
            onOpenTask={(id) => navigate({ kind: "task", id })}
            managesWork={isPm === true || isAdmin === true ? true : isPm === null || isAdmin === null ? null : false}
          />
        )}
      </main>
      {/* Asks about a past date typed into any due or client date box (F170). */}
      <PastDatePrompt />
    </>
  );
}

/**
 * Whether what the URL points at is already in hand.
 *
 * Asked per route rather than "is anything loaded", because leftovers from the
 * previous view are not the thing being navigated TO. Checking `!project` while
 * opening a deliverable from a project page reads as loaded, falls past every
 * branch below, and renders the home page for a moment.
 */
function loaded(
  route: Route,
  task: TaskView | null,
  project: ProjectView | null,
  deliverable: DeliverableView | null,
): boolean {
  switch (route.kind) {
    case "task":
      return task !== null;
    case "project":
      return project !== null;
    case "deliverable":
      return deliverable !== null;
    case "mobilization":
      // Never "loaded": it is only ever on its way to a task, or answered by `missing`.
      return false;
    default:
      // The list views fetch their own contents and show their own placeholder.
      return true;
  }
}

/*
 * `isWide` was removed on 4 Oct 2026. It answered "which screens get the extra
 * width", and the answer became "all of them": the task page showed a whole
 * workflow across the top and the dashboard showed three columns, while
 * everything else was a single column of cards that read better narrow. After
 * the refresh every screen is rows or two columns, so the distinction had
 * nothing left to distinguish.
 */
